import {
    Box,
    Grid,
    GridItem,
    Heading,
    Text,
    Badge,
    Button,
    HStack,
    Icon,
    useColorModeValue,
    Divider,
} from '@chakra-ui/react';
import { MdDescription, MdOpenInNew, MdCalendarToday, MdAttachMoney, MdBusiness } from 'react-icons/md';

interface OverviewTabProps {
    item: any;
    onOpenPreview: (page?: number) => void;
}

export function OverviewTab({ item, onOpenPreview }: OverviewTabProps) {
    const panelBg = useColorModeValue('white', 'gray.800');
    const subtleBg = useColorModeValue('gray.50', 'gray.900');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const mutedText = useColorModeValue('gray.500', 'gray.400');
    const labelColor = useColorModeValue('gray.600', 'gray.400');

    return (
        <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap={6}>
            {/* Left Column: Intelligence & Scope */}
            <GridItem display="flex" flexDirection="column" gap={6}>

                {/* Bid Intelligence */}
                <Box p={6} bg={panelBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
                    <Heading size="md" mb={6} display="flex" alignItems="center" gap={2}>
                        <Icon as={MdBusiness} color="brand.500" />
                        Bid Intelligence
                    </Heading>
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase" mb={1}>Agency</Text>
                            <Text fontSize="md" fontWeight="medium">{item.agency || 'N/A'}</Text>
                        </GridItem>
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase" mb={1}>Reference ID</Text>
                            <Text fontSize="md" fontFamily="mono" color="gray.500">{item.item_code || 'N/A'}</Text>
                        </GridItem>

                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase" mb={1}>Place of Performance</Text>
                            <Text fontSize="md">{item.location || 'Not Specified'}</Text>
                        </GridItem>
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase" mb={1}>Pre-Bid Meeting</Text>
                            <Text fontSize="md">
                                {item.pre_bid_meeting
                                    ? new Date(item.pre_bid_meeting).toLocaleDateString(undefined, { dateStyle: 'long', timeStyle: 'short' })
                                    : 'None / Not Listed'
                                }
                            </Text>
                        </GridItem>

                        <GridItem colSpan={{ base: 1, md: 2 }}>
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase" mb={2}>Set-Asides & Compliance</Text>
                            <HStack wrap="wrap" spacing={2}>
                                {item.protected_markets ? (
                                    <Badge colorScheme="purple" variant="subtle" px={2} py={1} borderRadius="md">{item.protected_markets}</Badge>
                                ) : (
                                    <Text fontSize="sm" color={mutedText}>No specific set-asides listed.</Text>
                                )}
                                {item.compliance_goals && (
                                    <Badge colorScheme="blue" variant="subtle" px={2} py={1} borderRadius="md">{item.compliance_goals}</Badge>
                                )}
                            </HStack>
                        </GridItem>
                    </Grid>
                </Box>

                {/* Scope of Work */}
                <Box p={6} bg={panelBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
                    <Heading size="md" mb={4}>Scope of Work</Heading>
                    <Box
                        fontSize="sm"
                        lineHeight="1.8"
                        whiteSpace="pre-wrap"
                        color={useColorModeValue('gray.700', 'gray.300')}
                        bg={subtleBg}
                        p={4}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        {item.description || 'No detailed description available.'}
                    </Box>
                    {item.contractor && (
                        <Box mt={4} p={3} bg="blue.50" _dark={{ bg: 'blue.900' }} borderRadius="md">
                            <Text fontSize="xs" fontWeight="bold" color="blue.600" _dark={{ color: 'blue.200' }}>Incumbent / Contractor</Text>
                            <Text fontSize="sm" fontWeight="medium" color="blue.800" _dark={{ color: 'blue.100' }}>{item.contractor}</Text>
                        </Box>
                    )}
                </Box>
            </GridItem>

            {/* Right Column: Key Stats & Docs */}
            <GridItem display="flex" flexDirection="column" gap={6}>
                {/* Key Metrics */}
                <Box p={6} bg={panelBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
                    <Heading size="sm" mb={4} color={mutedText} textTransform="uppercase" letterSpacing="wide">Key Metrics</Heading>

                    <Box mb={6}>
                        <HStack mb={1}>
                            <Icon as={MdCalendarToday} color="brand.500" />
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase">Deadline</Text>
                        </HStack>
                        <Text fontSize="xl" fontWeight="bold" color={item.submission_deadline ? 'brand.500' : 'gray.500'}>
                            {item.submission_deadline
                                ? new Date(item.submission_deadline).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
                                : 'Open / Rolling'
                            }
                        </Text>
                        {item.submission_deadline && (
                            <Text fontSize="sm" color={mutedText} mt={1}>
                                Due by {new Date(item.submission_deadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Text>
                        )}
                    </Box>

                    <Divider my={4} />

                    <Box>
                        <HStack mb={1}>
                            <Icon as={MdAttachMoney} color="green.500" />
                            <Text fontSize="xs" fontWeight="bold" color={labelColor} textTransform="uppercase">Est. Value</Text>
                        </HStack>
                        <Text fontSize="2xl" fontWeight="bold" color="gray.700" _dark={{ color: 'gray.200' }}>
                            {item.unit_price > 0 ? `$${Number(item.unit_price).toLocaleString()}` : 'See Docs'}
                        </Text>
                    </Box>
                </Box>

                {/* Documents */}
                <Box p={6} bg={panelBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
                    <HStack mb={4} spacing={2}>
                        <Icon as={MdDescription} color="brand.500" boxSize={5} />
                        <Heading size="md">Documents</Heading>
                    </HStack>

                    {item.documents_url ? (
                        <Box>
                            <Button
                                w="full"
                                mb={3}
                                leftIcon={<MdDescription />}
                                colorScheme="brand"
                                onClick={() => onOpenPreview()}
                            >
                                View PDF
                            </Button>
                            {item.source_url && (
                                <Button
                                    as="a"
                                    href={item.source_url}
                                    target="_blank"
                                    w="full"
                                    variant="outline"
                                    rightIcon={<MdOpenInNew />}
                                >
                                    Source Portal
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <Text fontSize="sm" color={mutedText}>No documents linked yet.</Text>
                    )}
                </Box>

                {/* AI Summary Card */}
                {item.ai_notes && (
                    <Box p={6} bg="purple.50" _dark={{ bg: 'purple.900' }} borderRadius="xl" borderWidth="1px" borderColor="purple.200">
                        <Heading size="sm" mb={2} color="purple.700" _dark={{ color: 'purple.200' }}>AI Summary</Heading>
                        <Text fontSize="sm" color="purple.800" _dark={{ color: 'purple.100' }} noOfLines={10}>
                            {item.ai_notes}
                        </Text>
                    </Box>
                )}
            </GridItem>
        </Grid>
    );
}
